/**
 * The `?` beside the word "Actions" in a table header, opening a legend that
 * defines each icon in that column — real glyph, name, one-line definition.
 *
 * Definitions come from actionIconGlossary.ts (one definition per meaning,
 * app-wide); callers declare WHICH meanings their column renders, in the same
 * left-to-right order as the buttons, right next to the cell renderer that
 * draws them. Contextual markdown can't render lucide glyphs, which is why
 * this is a component and not a <HelpTrigger> content key — the Help Center
 * depth lives in the icon-glossary article the modal footer links to.
 *
 * Adoption threshold (PRD D1): only columns offering more than View/Delete
 * get the trigger. When it renders, the legend still lists every icon in the
 * column, view and delete included.
 *
 * PRD: Documentation/ACTION_ICONS_HELP_PRD.md
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { IHeaderParams } from 'ag-grid-community';
import HelpModal from '@shared/ui/HelpModal';
import { HelpIcon, OpenExternalIcon } from './icons';
import { ACTION_ICON_GLOSSARY, type ActionIconKey } from './actionIconGlossary';

/** A bare key, or a key with a surface-specific note ("Authors only."). */
export type ActionsHelpIcon = ActionIconKey | { key: ActionIconKey; note?: string };

interface ActionsHeaderHelpProps {
  /** The column's meanings, in the buttons' left-to-right order. */
  icons: ActionsHelpIcon[];
  /**
   * 'header' = light-on-purple for StandardTable/GroupedTable/AG Grid header
   * bars; 'default' = the tertiary grey HelpTrigger uses on plain surfaces.
   */
  tone?: 'header' | 'default';
  /** Modal title; override when "Actions in this table" reads wrong. */
  title?: string;
  /**
   * The footer "Icon Glossary" link targets /help, a protected route — pass
   * false on surfaces anonymous visitors reach (the public calendar).
   */
  glossaryLink?: boolean;
  className?: string;
}

const toneClasses = {
  header: 'text-white/70 hover:text-white focus:ring-white/70',
  default:
    'text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 focus:ring-primary-500',
};

const ActionsHeaderHelp: React.FC<ActionsHeaderHelpProps> = ({
  icons,
  tone = 'header',
  title = 'Actions in this table',
  glossaryLink = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={`inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${toneClasses[tone]} ${className}`}
        aria-label="What do these actions do?"
        title="What do these actions do?"
      >
        <HelpIcon className="w-4 h-4" />
      </button>

      {/* Gated on isOpen so closed legends cost nothing per table render. */}
      {isOpen && (
      <HelpModal
        isOpen={isOpen}
        title={title}
        onClose={() => setIsOpen(false)}
        closeLabel="Got it"
        action={
          glossaryLink ? (
            <Link
              to="/help?article=icon-glossary"
              className="mr-auto inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              onClick={() => setIsOpen(false)}
            >
              <OpenExternalIcon className="w-4 h-4" />
              Icon Glossary
            </Link>
          ) : undefined
        }
      >
        <ul className="space-y-3">
          {icons
            .map((item) => {
              const key = typeof item === 'string' ? item : item.key;
              const note = typeof item === 'string' ? undefined : item.note;
              return { key, note, ...ACTION_ICON_GLOSSARY[key] };
            })
            .map(({ key, note, Icon, label, definition }) => (
            <li key={key} className="flex items-start gap-3">
              <span
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border dark:border-border-dark bg-surface-alt dark:bg-surface-dark-alt text-text-secondary dark:text-text-dark-secondary"
                aria-hidden="true"
              >
                <Icon className="w-4 h-4" />
              </span>
              <span>
                <strong className="font-semibold text-text-primary dark:text-text-dark-primary">
                  {label}
                </strong>{' '}
                — {definition}
                {note ? <span className="text-text-tertiary dark:text-text-dark-tertiary"> {note}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </HelpModal>
      )}
    </>
  );
};

export default ActionsHeaderHelp;

/**
 * AG Grid header for the ACTIONS column: the uppercase label plus the legend
 * trigger. Register via headerComponent on the columnDef; the icon keys come
 * from gridContext.actionsHelpIcons — declared in the consumer right beside
 * the context.actions array whose buttons they describe, because the same
 * columnDef factory serves surfaces with different action sets (directory:
 * view+track; workspace tab: priority+stop-tracking).
 */
export const ActionsGridHeader: React.FC<IHeaderParams> = (props) => {
  const icons: ActionsHelpIcon[] = (props.context as any)?.actionsHelpIcons ?? [];
  return (
    <div className="flex w-full items-center justify-between gap-1">
      <span>Actions</span>
      {icons.length > 0 && <ActionsHeaderHelp icons={icons} tone="header" />}
    </div>
  );
};
